import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TdsPanel from "@/components/TdsPanel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Upload, ShieldCheck, BadgeCheck, XCircle, CreditCard } from "lucide-react";
import InvoiceUploader, { InvoiceLinks } from "@/components/InvoiceUploader";
import TaxBlock from "@/components/TaxBlock";
import { ChainButton } from "@/components/ApprovalChain";
import ChainActionDialog from "@/components/ChainActionDialog";
import { RequestDetailsLoader } from "@/components/RequestDetailsPanel";
import { ExpenseLinesNotice } from "@/components/ExpenseLinesNotice";
import { SentBackBanner, ResubmitFooter, RejectedNote, hlFor, diffList, fileChange, useFileNames } from "@/components/EditResubmit";
import { PaymentFields, paymentMissing, useRkfBanks } from "@/components/DisburseDialog";

const statusColor = {
  Submitted: "bg-amber-100 text-amber-700",
  PMEndorsed: "bg-cyan-100 text-cyan-700",
  AccountsVerified: "bg-blue-100 text-blue-700",
  Approved: "bg-indigo-100 text-indigo-700",
  Paid: "bg-emerald-100 text-emerald-700",
  Rejected: "bg-red-100 text-red-700",
};
const payColor = {
  Pending: "bg-slate-100 text-slate-700",
  Scheduled: "bg-cyan-100 text-cyan-700",
  Processed: "bg-emerald-100 text-emerald-700",
  Failed: "bg-red-100 text-red-700",
  Returned: "bg-orange-100 text-orange-700",
};

const monthsList = () => {
  const arr = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    arr.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return arr;
};

export default function Reimbursements() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mine, setMine] = useState([]);
  const [editing, setEditing] = useState(null);
  const [copyFrom, setCopyFrom] = useState(null);
  const [invShown, setInvShown] = useState(null);
  const [payees, setPayees] = useState([]);
  const [tds, setTds] = useState([]);
  const [open, setOpen] = useState(false);

  const isAccounts = ["Admin", "Accounts"].includes(user?.role);
  const isApprover = ["Admin", "Approver", "Authorizer"].includes(user?.role);
  const [tdsFor, setTdsFor] = useState(null);
  const [target, setTarget] = useState(null);
  const act = (r) => setTarget({ kind: "reimbursement", id: r.id });
  const isPM = ["Admin", "ProjectManager"].includes(user?.role);

  useEffect(() => {
    api.get("/projects").then(r => { setProjects(r.data); if (r.data.length) setSelectedProject(r.data[0].id); });
    api.get("/v2/tds-sections").then(r => setTds(r.data));
  }, []);

  // Payees = active team members across every project the user can see; each carries its projects.
  useEffect(() => {
    if (!open && !editing && !copyFrom) return;
    api.get("/reimbursement-payees").then(r => setPayees(r.data || [])).catch(() => setPayees([]));
  }, [open, editing, copyFrom]);

  const loadMine = () => api.get("/my-requests").then(r => setMine((r.data.reimbursements || []).filter(x => x.status === "SentBack"))).catch(() => {});
  useEffect(() => {
    loadMine();
    const eid = new URLSearchParams(window.location.search).get("edit");
    if (eid) api.get(`/reimbursements/${eid}`).then(r => { if (r.data.status === "SentBack" && r.data.requester_id === user?.id) setEditing(r.data); }).catch(() => {});
    const cid = new URLSearchParams(window.location.search).get("copy");
    if (cid) api.get(`/reimbursements/${cid}`).then(r => { if (r.data.status === "Rejected" && r.data.requester_id === user?.id) setCopyFrom(r.data); }).catch(() => {});
  }, []);
  const openEdit = (r) => api.get(`/reimbursements/${r.id}`).then(x => setEditing(x.data));

  const load = async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const rs = await api.get(`/reimbursements?project_id=${selectedProject}`);
      setItems(rs.data);
    } finally { setLoading(false); }
    loadMine();
  };
  useEffect(() => { load(); }, [selectedProject]);

  const doAction = async (id, action, payload = {}) => {
    let extra = {};
    if (action === "reject") {
      const reason = window.prompt("Reason for rejection (required)");
      if (!reason || !reason.trim()) return;
      extra = { comment: reason };
    }
    try {
      await api.post(`/reimbursements/${id}/${action}`, { comment: "", ...payload, ...extra });
      toast.success(`Marked ${action}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Money · Reimbursement Claims</div>
          <h1 className="font-display text-3xl font-extrabold mt-1">Reimbursement Claims</h1>
          <p className="text-sm text-slate-500 mt-1">Claim back money you spent from your own pocket for a project (cabs, meals, small vendor bills). Different from <b>Project Expenses</b> where the org pays the vendor directly.</p>
          <p className="text-sm text-slate-500 mt-1">Endorse (Project PM) → Authorize → Accounts Verify → Final (Super Admin; small claims final at Verify) → Payment (UTR)</p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <div className="min-w-[200px]">
            <Label>Project</Label>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger data-testid="rmb-project-select"><SelectValue /></SelectTrigger>
              <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-[#0F172A]" data-testid="new-rmb-btn"><Plus className="w-4 h-4 mr-1" /> New Request</Button></DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              {open && <RmbForm defaultProjectId={selectedProject} payees={payees} currentUserId={user?.id} currentUserRole={user?.role} onSaved={(pid, doc) => { setOpen(false); if (doc?.inv_ref) setInvShown(doc.inv_ref); if (pid && pid !== selectedProject) setSelectedProject(pid); else load(); }} />}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs defaultValue="all">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="all" data-testid="rtab-all">All ({loading ? "…" : items.length})</TabsTrigger>
          <TabsTrigger value="mine-action" data-testid="rtab-needs-action" className={mine.length ? "text-rose-700" : ""}>Needs my action ({mine.length})</TabsTrigger>
          {isPM && <TabsTrigger value="endorse" data-testid="rtab-endorse">PM Endorse ({items.filter(i => i.status === "Submitted" && i.can_pm_endorse).length})</TabsTrigger>}
          {isAccounts && <TabsTrigger value="verify" data-testid="rtab-verify">Verify ({items.filter(i => i.can_act && i.current_step === "verify").length})</TabsTrigger>}
          {isApprover && <TabsTrigger value="approve" data-testid="rtab-approve">Authorize / Final ({items.filter(i => i.can_act && ["authorize", "final"].includes(i.current_step)).length})</TabsTrigger>}
          {isAccounts && <TabsTrigger value="pay" data-testid="rtab-pay">Payments ({items.filter(i => i.status === "Approved" || (i.status === "Paid" && i.payment_status !== "Processed")).length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="all">{loading ? <Card><CardContent className="p-10 text-center text-sm text-slate-500" data-testid="rmb-loading">Loading claims…</CardContent></Card> : <RmbList items={items} userId={user?.id} onEdit={openEdit} />}</TabsContent>
        <TabsContent value="mine-action"><RmbList items={mine} userId={user?.id} onEdit={openEdit} emptyText="Nothing needs your action." /></TabsContent>
        {isPM && <TabsContent value="endorse">
          <RmbList items={items.filter(i => i.status === "Submitted" && i.can_pm_endorse)} actions={r => (
            <div className="text-right">
              <Button size="sm" onClick={() => act(r)} data-testid={`endorse-${r.id}`}>Endorse</Button>
              {r.pm_routing_note && <div className="text-[10px] text-amber-700 mt-1" data-testid={`rmb-routing-note-${r.id}`}>{r.pm_routing_note}</div>}
            </div>
          )} />
        </TabsContent>}
        {isAccounts && <TabsContent value="verify">
          <RmbList items={items.filter(i => i.can_act && i.current_step === "verify")} actions={r => (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => act(r)} className="bg-blue-600" data-testid={`verify-${r.id}`}><ShieldCheck className="w-3.5 h-3.5 mr-1" />Verify</Button>
            </div>
          )} />
        </TabsContent>}
        {isApprover && <TabsContent value="approve">
          <RmbList items={items.filter(i => i.can_act && ["authorize", "final"].includes(i.current_step))} actions={r => (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => act(r)} className="bg-emerald-600" data-testid={`approve-${r.id}`}><BadgeCheck className="w-3.5 h-3.5 mr-1" />{r.current_step === "authorize" ? "Authorize" : "Final approve"}</Button>
            </div>
          )} />
        </TabsContent>}
        {isAccounts && <TabsContent value="pay">
          <PaymentBatch items={items.filter(i => i.status === "Approved" || i.status === "Paid")} onSaved={load} userId={user?.id} />
        </TabsContent>}
      </Tabs>
      <TdsPanel open={!!tdsFor} onOpenChange={(o) => !o && setTdsFor(null)} kind="reimbursement" item={tdsFor} onSaved={load} />
      <ChainActionDialog target={target} onClose={() => setTarget(null)} onDone={load} />
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); window.history.replaceState(null, "", "/app/reimbursements"); } }}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="rmb-edit-dialog">
          {editing && <RmbForm editing={editing} defaultProjectId={editing.project_id} payees={payees} currentUserId={user?.id} currentUserRole={user?.role}
            onSaved={() => { setEditing(null); window.history.replaceState(null, "", "/app/reimbursements"); load(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!copyFrom} onOpenChange={(o) => { if (!o) { setCopyFrom(null); window.history.replaceState(null, "", "/app/reimbursements"); } }}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="rmb-copy-dialog">
          {copyFrom && <RmbForm copyFrom={copyFrom} defaultProjectId={copyFrom.project_id} payees={payees} currentUserId={user?.id} currentUserRole={user?.role}
            onSaved={(pid, doc) => { setCopyFrom(null); window.history.replaceState(null, "", "/app/reimbursements"); if (doc?.inv_ref) setInvShown(doc.inv_ref); load(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!invShown} onOpenChange={(o) => !o && setInvShown(null)}>
        <DialogContent data-testid="inv-ref-dialog">
          <DialogHeader><DialogTitle>Claim submitted</DialogTitle></DialogHeader>
          <p className="text-sm">Write this number on your bill:</p>
          <div className="flex items-center gap-2"><span className="font-mono text-xl font-bold" data-testid="inv-ref-value">{invShown}</span>
            <Button size="sm" variant="outline" onClick={async () => { try { await navigator.clipboard.writeText(invShown); toast.success("Copied"); } catch { toast.error("Copy failed – please note the number manually"); } }} data-testid="inv-ref-copy">Copy</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RmbForm({ defaultProjectId, payees, currentUserId, currentUserRole, onSaved, editing, copyFrom }) {
  const src = editing || copyFrom;
  const initial = React.useMemo(() => src ? {
    payee_id: src.payee_id || "", project_id: src.project_id || "", head_id: src.head_id || "", budget_line_id: src.budget_line_id || "",
    expense_month: src.expense_month, tds_section_code: "NONE", tds_rate: 0, tds_override_reason: "",
    invoice_number: src.invoice_number || "", invoice_date: (src.invoice_date || "").slice(0, 10), purpose: src.purpose || "", late_reason: src.late_reason || "",
    no_vendor_bill_no: !!src.no_vendor_bill_no,
    lines: (src.particulars || []).map(p => ({ description: p.description, qty: p.qty || 0, rate: p.rate || 0, amount: p.amount || 0, gst_rate: p.gst_rate || 0 })),
    round_off: src.round_off || 0,
    _ui_totals: { sub: 0, totalGst: 0, invoiceTotal: src.invoice_total || 0, tdsAmount: 0, netPayable: 0 },
  } : null, [src]);
  const [f, setF] = useState(initial || {
    payee_id: "", project_id: "", head_id: "", budget_line_id: "", expense_month: monthsList()[0],
    tds_section_code: "NONE", tds_rate: 0, tds_override_reason: "", no_vendor_bill_no: false,
    invoice_number: "", invoice_date: "", purpose: "", late_reason: "",
    lines: [{ description: "", qty: 0, rate: 0, amount: 0, gst_rate: 0 }],
    round_off: 0,
    _ui_totals: { sub: 0, totalGst: 0, invoiceTotal: 0, tdsAmount: 0, netPayable: 0 },
  });
  const skipReset = useRef(!!src);
  // Default payee → the logged-in user if they're on the project team.
  useEffect(() => {
    if (f.payee_id) return;
    const mine = (payees || []).find(p => p.linked_user_id === currentUserId);
    if (mine?.payee_id) {
      setF(prev => ({ ...prev, payee_id: mine.payee_id,
        tds_section_id: mine.tds_section_id || "",
        tds_rate: mine.default_tds_rate || 0 }));
    }
  }, [payees, currentUserId]);
  const selectedPayee = (payees || []).find(p => p.payee_id === f.payee_id);
  const [busy, setBusy] = useState(false);
  const inflight = React.useRef(false);
  const bankMissing = !!selectedPayee?.bank_details_missing;
  const payeeProjects = selectedPayee?.projects || [];
  const projectId = f.project_id;
  // Auto-select the project when the payee is on exactly one team (or on the page's current project).
  useEffect(() => {
    if (!selectedPayee) return;
    if (skipReset.current) { skipReset.current = false; return; }
    const ps = selectedPayee.projects || [];
    const pick = ps.length === 1 ? ps[0].id : "";
    setF(prev => ({ ...prev, project_id: ps.some(p => p.id === prev.project_id) ? prev.project_id : pick, head_id: "", budget_line_id: "" }));
  }, [f.payee_id]);
  const [lines, setLines] = useState(null);
  useEffect(() => {
    setLines(null);
    if (!projectId) return;
    api.get(`/projects/${projectId}/expense-lines`).then(r => setLines(r.data)).catch(() => setLines({ heads: [], message: "Could not load budget lines for this project." }));
  }, [projectId]);
  const heads = lines?.heads || [];
  const [invoiceFiles, setInvoiceFiles] = useState((src?.invoice_file_ids || []).map(id => ({ id, original_filename: "Current attachment" })));
  useFileNames(src?.invoice_file_ids, setInvoiceFiles);
  const linesKey = (x) => JSON.stringify((x?.lines || []).map(l => [l.description, +l.qty || 0, +l.rate || 0, +l.amount || 0, +l.gst_rate || 0]));
  const hl = hlFor(editing?.sent_back_fields);
  const allSubs = heads.flatMap(h => (h.subtypes || []).map(s => ({ id: s.id, code: s.code, head: h.code })));
  const origLine = useRef(null);
  useEffect(() => {
    if (editing && !origLine.current && projectId === editing.project_id && allSubs.length) {
      origLine.current = allSubs.find(x => x.id === editing.budget_line_id) || { code: "", head: "" };
    }
  }, [heads]);
  const [tdsSectionsV2, setTdsSectionsV2] = useState([]);
  const [gstRates, setGstRates] = useState([]);
  useEffect(() => {
    api.get("/v2/tds-sections").then(r => setTdsSectionsV2(r.data));
    api.get("/v2/gst-rates").then(r => setGstRates(r.data));
  }, []);
  const filteredLineOptions = ((heads.find(h => h.id === f.head_id) || {}).subtypes || []).map(s => ({
    id: s.id, label: `${s.code} · ${s.name}`, remaining: s.remaining || 0,
  }));
  const selectedLine = filteredLineOptions.find(l => l.id === f.budget_line_id);
  const invTotal = f._ui_totals?.invoiceTotal || 0;
  const ownBack = editing && f.budget_line_id === editing.budget_line_id ? (editing.invoice_total || 0) : 0;
  const exceeds = selectedLine && invTotal > selectedLine.remaining + ownBack;
  const curLine = allSubs.find(x => x.id === f.budget_line_id) || {};
  const projCode = (id) => (payeeProjects.find(p => p.id === id) || {}).code || "";
  const amtChanged = editing && Math.abs((editing.invoice_total || 0) - invTotal) > 0.01;
  const material = !!editing && (f.project_id !== editing.project_id || f.budget_line_id !== editing.budget_line_id || amtChanged);
  const changes = editing ? diffList([
    ["Payee", editing.payee_name, selectedPayee?.name || editing.payee_name],
    ["Project", projCode(editing.project_id), projCode(f.project_id)],
    ["Head", origLine.current?.head, curLine.head], ["Sub-type", origLine.current?.code, curLine.code],
    ["Amount", Number(editing.invoice_total || 0).toFixed(2), Number(invTotal || 0).toFixed(2)],
    ["Expense Month", initial.expense_month, f.expense_month], ["Vendor bill no.", initial.invoice_number, f.invoice_number],
    ["Bill date", initial.invoice_date, f.invoice_date], ["Purpose", initial.purpose, f.purpose], ["Late reason", initial.late_reason, f.late_reason],
  ], [linesKey(initial) !== linesKey(f) ? "Line items edited" : null, initial.no_vendor_bill_no !== !!f.no_vendor_bill_no ? "No-vendor-bill-number flag changed" : null,
    fileChange(editing.invoice_file_ids, invoiceFiles.map(x => x.id))]) : [];

  const body = () => ({
    payee_id: f.payee_id, charge_type: "project", project_id: projectId,
    budget_line_id: f.budget_line_id, expense_month: f.expense_month,
    particulars: f.lines.map(l => ({
      description: l.description, amount: parseFloat(l.amount || 0),
      qty: parseFloat(l.qty || 0), rate: parseFloat(l.rate || 0),
      gst_rate: parseFloat(l.gst_rate || 0),
    })),
    tds_section_id: f.tds_section_code && f.tds_section_code !== "NONE" ? f.tds_section_code : null,
    tds_rate: parseFloat(f.tds_rate || 0),
    invoice_number: f.no_vendor_bill_no ? "" : f.invoice_number, no_vendor_bill_no: !!f.no_vendor_bill_no, invoice_date: f.invoice_date || null,
    invoice_file_ids: invoiceFiles.map(x => x.id),
    purpose: f.purpose, late_reason: f.late_reason,
    round_off: parseFloat(f.round_off || 0),
  });
  const saveEdit = async (action, response) => {
    setBusy(true);
    try {
      await api.put(`/reimbursements/${editing.id}/sent-back-edit`, { ...body(), action, response_to_reviewer: response });
      toast.success(action === "draft" ? "Draft saved" : "Resubmitted");
      onSaved(projectId);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (editing) return;
    if (inflight.current) return;
    if (exceeds) { toast.error("Invoice total exceeds remaining balance"); return; }
    if (bankMissing) { toast.error("Bank details missing for this payee — Accounts to update first"); return; }
    inflight.current = true;
    setBusy(true);
    try {
      const r = await api.post("/reimbursements", body());
      toast.success("Submitted for approval");
      onSaved(projectId, r.data);
    } catch (err) { inflight.current = false; setBusy(false); toast.error(err.response?.data?.detail || "Failed"); }
  };

  const currentYm = monthsList()[0];
  const showLateReason = f.expense_month !== currentYm && f.expense_month < currentYm;

  return (
    <>
      <DialogHeader><DialogTitle>{editing ? `Edit & resubmit · ${editing.rmb_id}` : copyFrom ? `New claim · copied from ${copyFrom.rmb_id}` : "New Reimbursement Request"}</DialogTitle></DialogHeader>
      {editing && <SentBackBanner doc={editing} invRef={editing.inv_ref} />}
      {copyFrom && <p className="text-xs text-slate-600 rounded bg-slate-50 border p-2" data-testid="rmb-copy-note">Pre-filled from rejected {copyFrom.rmb_id}. Submitting creates a new claim with a new reference and INV number.</p>}
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div data-testid="qv-payee">
            <Label>Payee</Label>
            <Select value={f.payee_id} onValueChange={(v) => {
              const p = payees.find(x => x.payee_id === v);
              setF({ ...f, payee_id: v, tds_section_id: p?.tds_section_id || "", tds_rate: p?.default_tds_rate || 0 });
            }} disabled={payees.length === 0}>
              <SelectTrigger data-testid="rmb-payee-select"><SelectValue placeholder={payees.length ? "Select payee" : "No team members found"} /></SelectTrigger>
              <SelectContent>
                {payees.map(p => (
                  <SelectItem key={p.payee_id || p.linked_user_id} value={p.payee_id} disabled={!p.payee_id} data-testid={`rmb-payee-opt-${p.linked_user_id}`}>
                    <span>{p.name}{p.role_on_project === "ProjectManager" ? " · PM" : ""}</span>
                    {p.bank_details_missing && <span className="ml-2 text-[10px] text-rose-600">Bank details missing</span>}
                    {p.bank_pending_verification && <span className="ml-2 text-[10px] text-amber-600">Bank pending verification</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {bankMissing && (
              <div className="text-[11px] text-rose-700 mt-1" data-testid="rmb-bank-missing-warn">
                Bank details missing — ask the member to add them in My Profile, or Accounts to update before submit.
              </div>
            )}
            {selectedPayee?.bank_pending_verification && (
              <div className="text-[11px] text-amber-700 mt-1" data-testid="rmb-bank-pending-warn">
                Bank details awaiting Accounts verification — you can submit; payment waits until verified.
              </div>
            )}
          </div>
          <div className={hl("expense_month")}>
            <Label>Expense Month</Label>
            <Select value={f.expense_month} onValueChange={(v) => setF({ ...f, expense_month: v })}>
              <SelectTrigger data-testid="rmb-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{monthsList().map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Project *</Label>
          <div className={hl("project")}>
          <Select value={f.project_id} onValueChange={(v) => setF({ ...f, project_id: v, head_id: "", budget_line_id: "" })} disabled={!selectedPayee || payeeProjects.length <= 1}>
            <SelectTrigger data-testid="rmb-form-project-select"><SelectValue placeholder={selectedPayee ? "Select project" : "Select payee first"} /></SelectTrigger>
            <SelectContent>{payeeProjects.map(p => <SelectItem key={p.id} value={p.id} data-testid={`rmb-form-project-opt-${p.code}`}>{p.code} · {p.name}</SelectItem>)}</SelectContent>
          </Select>
          </div>
          <ExpenseLinesNotice lines={lines} testId="rmb-no-wb" />
        </div>
        <div className={`grid grid-cols-2 gap-3 ${hl("budget_line")}`}>
          <div>
            <Label>Head *</Label>
            <Select value={f.head_id} onValueChange={(v) => setF({ ...f, head_id: v, budget_line_id: "" })} disabled={!heads.length}>
              <SelectTrigger data-testid="rmb-head-select"><SelectValue placeholder={projectId ? (heads.length ? "Select head" : "No heads available") : "Select project first"} /></SelectTrigger>
              <SelectContent>{heads.map(h => <SelectItem key={h.id} value={h.id} data-testid={`rmb-head-opt-${h.code}`}>{h.code} · {h.name}{(h.category_name || h.category_code) ? ` (${h.category_name || h.category_code})` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Sub-Type / Budget Line (Working)</Label>
            <Select value={f.budget_line_id} onValueChange={(v) => setF({ ...f, budget_line_id: v })} disabled={!f.head_id}>
              <SelectTrigger data-testid="rmb-line-select"><SelectValue placeholder="Select sub-type" /></SelectTrigger>
              <SelectContent>
                {filteredLineOptions.map(l => (
                  <SelectItem key={l.id} value={l.id} disabled={l.remaining <= 0} className={l.remaining <= 0 ? "opacity-50" : ""} data-testid={`rmb-line-opt-${l.id}`}>
                    {l.label} <span className={`text-xs ml-1 ${l.remaining <= 0 ? "text-slate-500" : "text-emerald-600"}`}>Remaining ₹{l.remaining.toLocaleString()}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedLine && <div className="text-xs text-slate-500 mt-1">Remaining: <span className="font-mono">₹ {selectedLine.remaining?.toLocaleString()}</span></div>}
          </div>
        </div>
        {showLateReason && (
          <div><Label>Late-claim Reason (required)</Label><Input required value={f.late_reason} onChange={(e) => setF({ ...f, late_reason: e.target.value })} /></div>
        )}

        {/* Shared tax block — line items + optional GST + TDS + summary. */}
        <div className={`${hl("particulars")} ${hl("amount")}`}>
        <TaxBlock
          vendor={{ gst_registered: f.lines.some(l => (parseFloat(l.gst_rate) || 0) > 0), pan: "employee", vendor_type: "Individual", state: "Delhi" }}
          rkfState="Delhi"
          gstRates={gstRates}
          tdsSections={tdsSectionsV2}
          value={f}
          onChange={setF}
          compactHeader
          tdsReadonly={!["Admin", "Accounts"].includes(currentUserRole)}
        />
        </div>

        <div className={`grid grid-cols-3 gap-3 ${hl("invoice_number")}`}>
          <div><Label>Vendor bill no.{f.no_vendor_bill_no ? "" : " *"}</Label><Input required={!f.no_vendor_bill_no} disabled={f.no_vendor_bill_no} value={f.no_vendor_bill_no ? "—" : f.invoice_number} onChange={(e) => setF({...f, invoice_number: e.target.value})} data-testid="rmb-invoice-number-input" />
            <label className="flex items-center gap-1.5 text-[11px] mt-1"><input type="checkbox" checked={!!f.no_vendor_bill_no} onChange={(e) => setF({ ...f, no_vendor_bill_no: e.target.checked })} data-testid="rmb-no-bill-no" /> No vendor bill number</label>
            {!f.no_vendor_bill_no && f.invoice_number && (/^0+$/.test(f.invoice_number.trim()) || (!/\d/.test(f.invoice_number) && f.invoice_number.trim().length > 3) || f.invoice_number.trim().split(/\s+/).length >= 3) && <p className="text-[11px] text-amber-700" data-testid="rmb-bill-no-warn">This doesn’t look like a bill number – tick “No vendor bill number” if the bill has none.</p>}
          </div>
          <div className={hl("invoice_date")}><Label>Invoice Date</Label><Input type="date" value={f.invoice_date} onChange={(e) => setF({...f, invoice_date: e.target.value})} data-testid="rmb-invoice-date" /></div>
          <div className={hl("purpose")}><Label>Purpose</Label><Input value={f.purpose} onChange={(e) => setF({...f, purpose: e.target.value})} data-testid="rmb-purpose" /></div>
        </div>
        <div className={hl("attachments")}><InvoiceUploader label="Invoice / Receipts (mandatory before verify)" files={invoiceFiles} onChange={setInvoiceFiles} testId="rmb-invoice" /></div>
        {selectedLine && (
          <div className={`text-xs px-3 py-2 rounded ${exceeds ? "bg-rose-50 text-rose-700 border border-rose-200" : "bg-slate-50 text-slate-600 border border-slate-200"}`}>
            Remaining working balance: <span className="font-mono">₹{selectedLine.remaining?.toLocaleString()}</span> → After this claim: <span className="font-mono">₹{(selectedLine.remaining - invTotal).toLocaleString()}</span>{exceeds && " · EXCEEDS!"}
          </div>
        )}
        {editing ? (
          <ResubmitFooter kind="reimbursement" doc={editing} changes={changes} material={material} busy={busy} onSave={saveEdit} onWithdrawn={() => onSaved(projectId)}
            blockReason={exceeds ? "Invoice total exceeds the remaining balance of this sub-type" : !f.budget_line_id ? "Pick a sub-type" : null} />
        ) : <DialogFooter><Button type="submit" disabled={busy || exceeds || bankMissing || !f.payee_id || !projectId || !f.budget_line_id} data-testid="submit-rmb-btn">{busy ? "Saving…" : "Submit"}</Button></DialogFooter>}
      </form>
    </>
  );
}

function RmbList({ items, actions, userId, onEdit, emptyText }) {
  if (!items.length) return <Card className="border-dashed"><CardContent className="p-10 text-center text-sm text-slate-500">{emptyText || "No claims here yet."}</CardContent></Card>;
  return (
    <div className="space-y-2">
      {items.map(r => (
        <Card key={r.id} data-testid={`rmb-${r.id}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{r.rmb_id}</span>
                  {r.inv_ref && <span className="font-mono text-xs bg-indigo-50 text-indigo-800 px-2 py-0.5 rounded" data-testid={`rmb-inv-${r.id}`}>{r.inv_ref}</span>}
                  <span className={`status-chip ${statusColor[r.status] || "bg-slate-100 text-slate-700"}`} data-testid={`rmb-status-${r.id}`}>{r.status_label || r.status}</span>
                  {r.resubmit_count > 0 && <span className="text-[10px] text-blue-700">Resubmitted ({r.resubmit_count === 1 ? "1st" : r.resubmit_count === 2 ? "2nd" : r.resubmit_count === 3 ? "3rd" : `${r.resubmit_count}th`} time)</span>}
                  <span className={`status-chip ${payColor[r.payment_status]}`}>Pay: {r.payment_status}</span>
                  <Badge variant="outline" className="text-[10px] font-mono">{r.expense_month}</Badge>
                </div>
                <div className="text-sm mt-1 font-medium">{r.payee_name} · Vendor bill {r.no_vendor_bill_no ? "—" : (r.invoice_number || "—")}</div>
                {r.status === "SentBack" && (
                  <div className="mt-2 rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-xs text-rose-800 flex items-center gap-2 flex-wrap" data-testid={`rmb-sentback-banner-${r.id}`}>
                    <span className="font-semibold">Sent back by {r.sent_back_by_name || "reviewer"}{r.sent_back_by_role ? `, ${r.sent_back_by_role}` : ""}:</span> {r.sent_back_reason}
                    {r.requester_id === userId && onEdit && <Button size="sm" className="h-7 bg-rose-600 hover:bg-rose-700" onClick={() => onEdit(r)} data-testid={`rmb-edit-btn-${r.id}`}>Edit & resubmit</Button>}
                  </div>
                )}
                {r.status === "Rejected" && <RejectedNote doc={r} copyLink={r.requester_id === userId ? `/app/reimbursements?copy=${r.id}` : null} testId={`rmb-rejected-${r.id}`} />}
                <div className="text-xs text-slate-500 mt-0.5">Requested by {r.requester_name}{r.late_reason ? ` · Late: ${r.late_reason}` : ""}</div>
                {r.invoice_file_ids?.length > 0 && <InvoiceLinks fileIds={r.invoice_file_ids} compact />}
              </div>
              <div className="text-right">
                <div className="font-mono font-bold">₹ {r.gross_amount?.toLocaleString()}</div>
                <div className="text-xs text-slate-500 font-mono">TDS − ₹{r.tds_amount?.toFixed(2)} · Payable ₹{r.payable_amount?.toLocaleString()}</div>
              </div>
              {actions && actions(r)}
              <ChainButton kind="reimbursement" id={r.id} testId={`rmb-chain-${r.id}`} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function PaymentBatch({ items, onSaved, userId }) {
  const [payOpen, setPayOpen] = useState(null);
  return (
    <div>
      <RmbList items={items} actions={r => (
        r.payment_status === "Processed" ? <span className="text-xs text-emerald-700">Paid · UTR {r.utr || "—"}{r.self_disbursed && <span className="ml-1 rounded border border-amber-300 bg-amber-100 px-1 text-amber-900" data-testid={`rmb-self-paid-${r.id}`}>Self-paid (Accounts)</span>}</span>
        : <Button size="sm" onClick={() => setPayOpen(r)} data-testid={`pay-${r.id}`}><CreditCard className="w-3.5 h-3.5 mr-1" /> {r.payee_user_id && r.payee_user_id === userId ? "Payment (own)" : "Payment"}</Button>
      )} />
      <Dialog open={!!payOpen} onOpenChange={(v) => !v && setPayOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto overscroll-contain">
          {payOpen && <PayForm r={payOpen} selfPay={!!payOpen.payee_user_id && payOpen.payee_user_id === userId} onSaved={() => { setPayOpen(null); onSaved(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PayForm({ r, onSaved, selfPay }) {
  const banks = useRkfBanks();
  const [pf, setPf] = useState({ pay_date: (r.pay_date || new Date().toISOString()).slice(0, 10), mode: r.pay_mode || "NEFT", utr: r.utr || "", paid_from_id: r.paid_from_id || "", proof: [], note: "" });
  const [status, setStatus] = useState(selfPay || r.payment_status !== "Pending" ? "Processed" : "Scheduled");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const miss = status === "Processed" ? (selfPay ? paymentMissing(pf, true) : !pf.utr.trim() ? "UTR / reference is required for Processed." : null) : null;
    if (miss) { setErr(miss); return; }
    setErr(""); setBusy(true);
    try {
      await api.post(`/reimbursements/${r.id}/payment`, { payment_status: status, utr: pf.utr, pay_date: pf.pay_date, mode: pf.mode,
        paid_from_id: pf.paid_from_id || null, proof_file_id: pf.proof[0]?.id || null });
      toast.success(selfPay ? "Paid – marked Self-disbursed, Super Admin notified" : "Payment updated");
      onSaved();
    } catch (e2) { setErr(e2.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <DialogHeader><DialogTitle>Update Payment · {r.rmb_id}</DialogTitle></DialogHeader>
      <details className="rounded border p-2" data-testid="pay-details"><summary className="text-sm cursor-pointer">Request details</summary><div className="mt-2"><RequestDetailsLoader kind="reimbursement" id={r.id} /></div></details>
      {selfPay && <p className="text-xs rounded border border-amber-300 bg-amber-50 p-2 text-amber-900 mt-2" data-testid="pay-self-note">Your own claim: allowed only after Final approval by the Super Admin, paid to your registered bank account, with UTR and proof. It is marked "Self-disbursed (Accounts)" and the Super Admin is notified.</p>}
      <form onSubmit={submit} className="space-y-3 mt-3">
        {!selfPay && <div>
          <Label>Payment Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="pay-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Scheduled">Scheduled</SelectItem>
              <SelectItem value="Processed">Processed</SelectItem>
              <SelectItem value="Failed">Failed</SelectItem>
              <SelectItem value="Returned">Returned</SelectItem>
            </SelectContent>
          </Select>
        </div>}
        <div className="text-xs text-slate-600 bg-slate-50 rounded p-2" data-testid="pay-tds-summary">Gross ₹{(r.gross_amount || 0).toLocaleString()} · TDS {r.tds_confirmed ? `${r.tds_rate}%` : "No TDS (default)"} · Net ₹{(r.tds_confirmed ? r.payable_amount : r.gross_amount || 0).toLocaleString()}</div>
        <PaymentFields f={pf} setF={(v) => { setErr(""); setPf(v); }} banks={banks} selfPay={selfPay} optional={!selfPay} testId="pay" />
        {err && <p className="text-xs text-rose-600" data-testid="pay-error">{err}</p>}
        <DialogFooter><Button type="submit" disabled={busy} data-testid="save-pay-btn">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
      </form>
    </>
  );
}
